import { useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { appPath } from '../../appConfig.ts'
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import {
  getImportSummary,
  listImportRuns,
  listDiscrepancies,
  resolveDiscrepancy,
} from '../../api/imports.ts'
import type {
  ImportRun,
  ImportDiscrepancy,
  ImportSummary,
  Resolution,
} from '../../types/imports.ts'
import { discrepancyMapLink } from '../../utils/discrepancyLink.ts'
import { runDisplayStatus, type RunTone } from './runStatus.ts'
import {
  DISCREPANCY_TYPES,
  DISCREPANCY_TYPE_LABELS,
  type DiscrepancyType,
} from '../gis-queue/types.ts'

/** Singular query-key entity name for a discrepancy's table (matches the detail
 *  components' query keys: ['meter', id] / ['transformer', id] / …). */
const TABLE_TO_ENTITY_KEY: Record<string, string> = {
  substations: 'substation',
  feeders: 'feeder',
  transformers: 'transformer',
  meters: 'meter',
}

/** After an Accept-GIS write, reflect the applied change: refresh the map, the
 *  data-quality rollup (relationship_source shifts), and the affected entity's
 *  own detail query so an open panel updates in place. */
function invalidateAcceptGisTargets(qc: QueryClient, row: ImportDiscrepancy) {
  qc.invalidateQueries({ queryKey: ['map'] })
  qc.invalidateQueries({ queryKey: ['data-quality'] })
  const entity = TABLE_TO_ENTITY_KEY[row.table_name]
  if (entity && row.record_id != null) {
    qc.invalidateQueries({ queryKey: [entity, row.record_id] })
  }
}

// =============================================================================
// Summary Cards
// =============================================================================

function SummaryCards({ summary }: { summary: ImportSummary }) {
  const pendingPct = summary.total_discrepancies > 0
    ? Math.round((summary.pending_discrepancies / summary.total_discrepancies) * 100)
    : 0

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="text-xs text-gray-500 uppercase tracking-wide">Import Runs</div>
        <div className="text-2xl font-bold mt-1">{summary.total_runs}</div>
        <div className="text-xs text-gray-400 mt-1">
          {summary.completed_runs} completed, {summary.failed_runs} failed
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="text-xs text-gray-500 uppercase tracking-wide">Total Discrepancies</div>
        <div className="text-2xl font-bold mt-1">{summary.total_discrepancies}</div>
        <div className="text-xs text-gray-400 mt-1">
          {summary.by_table.map(t => `${t.table_name}: ${t.count}`).join(', ') || 'None'}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="text-xs text-gray-500 uppercase tracking-wide">Pending Review</div>
        <div className="text-2xl font-bold text-amber-600 mt-1">{summary.pending_discrepancies}</div>
        <div className="text-xs text-gray-400 mt-1">
          {pendingPct}% of total
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="text-xs text-gray-500 uppercase tracking-wide">Last Sync</div>
        <div className="text-sm font-medium mt-1">
          {summary.last_run
            ? new Date(summary.last_run.started_at).toLocaleDateString()
            : 'Never'}
        </div>
        {summary.last_run && (
          <div className="text-xs text-gray-400 mt-1">
            +{summary.last_run.records_added} / ~{summary.last_run.records_updated} / ={summary.last_run.records_unchanged}
          </div>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// Import Run List
// =============================================================================

/** Tone -> badge classes for the C3 truth-table display (GIS Phase 2A Task
 *  12). `runDisplayStatus` decides label + tone; this is purely visual. */
const RUN_TONE_STYLES: Record<RunTone, string> = {
  neutral: 'bg-gray-100 text-gray-800',
  active: 'bg-blue-100 text-blue-800',
  warn: 'bg-amber-100 text-amber-800',
  done: 'bg-green-100 text-green-800',
  fail: 'bg-red-100 text-red-800',
}

/** Renders the truthful C3 status label (spec §6's 9-row truth table via
 *  `runDisplayStatus`) instead of the raw `status` column, which used to
 *  read "running" for a legacy sync stuck for days (the 2026-07-21 incident
 *  this closes) exactly as it did for one genuinely in progress. */
function RunStatusBadge({ run, now }: { run: ImportRun; now: Date }) {
  const { label, tone } = runDisplayStatus(run, now)
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${RUN_TONE_STYLES[tone]}`}>
      {label}
    </span>
  )
}

function ImportRunList({
  runs,
  selectedRunId,
  onSelectRun,
}: {
  runs: ImportRun[]
  selectedRunId: number | null
  onSelectRun: (id: number | null) => void
}) {
  if (runs.length === 0) {
    return (
      <div className="text-sm text-gray-500 py-4 text-center">
        No import runs yet. Run <code className="bg-gray-100 px-1 rounded">python scripts/sync_gis.py</code> to start.
      </div>
    )
  }

  // Computed once per render, not per row -- a page sitting open across the
  // 24h stale threshold will pick up the new "now" on its next re-render
  // (queries already refetch on the same staleTime cadence).
  const now = new Date()

  return (
    <div className="space-y-2">
      {runs.map(run => (
        <button
          key={run.id}
          onClick={() => onSelectRun(selectedRunId === run.id ? null : run.id)}
          className={`w-full text-left p-3 rounded-lg border transition-colors ${
            selectedRunId === run.id
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Run #{run.id}</span>
            <RunStatusBadge run={run} now={now} />
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {new Date(run.started_at).toLocaleString()}
            {run.run_by && ` by ${run.run_by}`}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            +{run.records_added} added, ~{run.records_updated} updated, ={run.records_unchanged} unchanged
            {run.discrepancies_found > 0 && (
              <span className="text-amber-600 ml-1">({run.discrepancies_found} discrepancies)</span>
            )}
          </div>
          {run.discrepancies_found > 0 && (
            <Link
              to={appPath('/gis-queue')}
              onClick={e => e.stopPropagation()}
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline mt-1 inline-block"
              title="Triage this run's findings in the GIS queue"
            >
              Triage in GIS Queue &rarr;
            </Link>
          )}
        </button>
      ))}
    </div>
  )
}

// =============================================================================
// Discrepancy Table
// =============================================================================

function TypeBadge({ type }: { type: string }) {
  const styles: Record<string, string> = {
    relationship_conflict: 'bg-red-100 text-red-700',
    relationship_ambiguous: 'bg-purple-100 text-purple-700',
    relationship_unresolved: 'bg-purple-100 text-purple-700',
    attribute_changed: 'bg-yellow-100 text-yellow-700',
    identity_conflict: 'bg-rose-100 text-rose-700',
    identity_key_change: 'bg-rose-100 text-rose-700',
    missing_from_gis: 'bg-orange-100 text-orange-700',
    new_in_gis: 'bg-blue-100 text-blue-700',
  }
  // Compact labels for the four legacy types; the shared gis-queue vocabulary
  // (plain language, no snake_case) covers the other four and any future value.
  const labels: Record<string, string> = {
    relationship_conflict: 'Conflict',
    attribute_changed: 'Changed',
    missing_from_gis: 'Missing',
    new_in_gis: 'New',
  }
  const label =
    labels[type] ?? DISCREPANCY_TYPE_LABELS[type as DiscrepancyType] ?? type
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles[type] || 'bg-gray-100 text-gray-700'}`}>
      {label}
    </span>
  )
}

function ResolutionBadge({ resolution }: { resolution: string }) {
  const styles: Record<string, string> = {
    pending: 'bg-amber-100 text-amber-700',
    accept_gis: 'bg-green-100 text-green-700',
    keep_platform: 'bg-blue-100 text-blue-700',
    reviewed: 'bg-gray-100 text-gray-700',
  }
  const labels: Record<string, string> = {
    pending: 'Pending',
    accept_gis: 'Accept GIS',
    keep_platform: 'Keep Platform',
    reviewed: 'Reviewed',
  }
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles[resolution] || 'bg-gray-100 text-gray-700'}`}>
      {labels[resolution] || resolution}
    </span>
  )
}

/** The GIS-ID / record-id cell: a map deep-link when the row points at a
 *  locatable platform record, plain text otherwise (no dead link, item 5). */
function DiscrepancyIdLink({ discrepancy }: { discrepancy: ImportDiscrepancy }) {
  const link = discrepancyMapLink(discrepancy)
  const label =
    discrepancy.gis_id ||
    (discrepancy.record_id != null ? `#${discrepancy.record_id}` : '--')
  if (!link) return <span>{label}</span>
  return (
    <Link
      to={appPath(link)}
      className="text-blue-600 hover:text-blue-800 hover:underline"
      title="Show this feature on the map"
    >
      {label}
    </Link>
  )
}

function DiscrepancyTable({
  discrepancies,
  onResolve,
  resolving,
}: {
  discrepancies: ImportDiscrepancy[]
  onResolve: (discrepancy: ImportDiscrepancy, resolution: Resolution) => void
  resolving: boolean
}) {
  if (discrepancies.length === 0) {
    return (
      <div className="text-sm text-gray-500 py-8 text-center">
        No discrepancies match the current filters.
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
            <th className="p-2">Type</th>
            <th className="p-2">Table</th>
            <th className="p-2">GIS ID</th>
            <th className="p-2">Field</th>
            <th className="p-2">Platform Value</th>
            <th className="p-2">GIS Value</th>
            <th className="p-2">Status</th>
            <th className="p-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {discrepancies.map(d => (
            <tr key={d.id} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="p-2"><TypeBadge type={d.discrepancy_type} /></td>
              <td className="p-2 font-mono text-xs">{d.table_name}</td>
              <td className="p-2 font-mono text-xs max-w-[120px] truncate" title={d.gis_id || ''}>
                <DiscrepancyIdLink discrepancy={d} />
              </td>
              <td className="p-2 text-xs">{d.field_name || '--'}</td>
              <td className="p-2 text-xs max-w-[150px] truncate" title={d.platform_value || ''}>
                {d.platform_value || '--'}
              </td>
              <td className="p-2 text-xs max-w-[150px] truncate" title={d.gis_value || ''}>
                {d.gis_value || '--'}
              </td>
              <td className="p-2"><ResolutionBadge resolution={d.resolution} /></td>
              <td className="p-2">
                {d.resolution === 'pending' && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => onResolve(d, 'accept_gis')}
                      disabled={resolving}
                      className="text-xs px-2 py-1 rounded bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50"
                      title="Accept the GIS value"
                    >
                      GIS
                    </button>
                    <button
                      onClick={() => onResolve(d, 'keep_platform')}
                      disabled={resolving}
                      className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                      title="Keep the platform value"
                    >
                      Keep
                    </button>
                    <button
                      onClick={() => onResolve(d, 'reviewed')}
                      disabled={resolving}
                      className="text-xs px-2 py-1 rounded bg-gray-50 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                      title="Mark as reviewed (no action)"
                    >
                      OK
                    </button>
                  </div>
                )}
                {d.resolution !== 'pending' && d.resolved_by && (
                  <span className="text-xs text-gray-400">by {d.resolved_by}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// =============================================================================
// Action explainer (UX Stage 1 C3): what each triage button actually does.
// =============================================================================

function ActionExplainer() {
  return (
    <details className="mb-4 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
      <summary className="cursor-pointer font-medium text-gray-700">
        What do these actions do?
      </summary>
      <ul className="mt-2 space-y-1 list-disc pl-4">
        <li>
          <span className="font-medium">GIS</span> — writes the GIS value into
          the platform record. The map, detail panels, and analyses pick up the
          change immediately. Needs the override permission.
        </li>
        <li>
          <span className="font-medium">Keep</span> — keeps the platform value
          and records that GIS disagrees. Nothing changes in the data.
        </li>
        <li>
          <span className="font-medium">OK</span> — closes the finding with no
          data change, for rows that need no action.
        </li>
      </ul>
      <p className="mt-2 text-gray-500">
        Every decision is recorded with your name. Bulk changes run from the
        GIS Queue, which previews the matched set and asks you to type the
        count before executing.
      </p>
    </details>
  )
}

// =============================================================================
// Filters
// =============================================================================

function Filters({
  resolution,
  discrepancyType,
  tableName,
  onResolutionChange,
  onTypeChange,
  onTableChange,
}: {
  resolution: string
  discrepancyType: string
  tableName: string
  onResolutionChange: (v: string) => void
  onTypeChange: (v: string) => void
  onTableChange: (v: string) => void
}) {
  return (
    <div className="flex flex-wrap gap-3 mb-4">
      <select
        value={resolution}
        onChange={e => onResolutionChange(e.target.value)}
        className="text-sm border border-gray-300 rounded-md px-3 py-1.5"
      >
        <option value="">All Resolutions</option>
        <option value="pending">Pending</option>
        <option value="accept_gis">Accept GIS</option>
        <option value="keep_platform">Keep Platform</option>
        <option value="reviewed">Reviewed</option>
      </select>

      <select
        value={discrepancyType}
        onChange={e => onTypeChange(e.target.value)}
        className="text-sm border border-gray-300 rounded-md px-3 py-1.5"
      >
        <option value="">All Types</option>
        {DISCREPANCY_TYPES.map(t => (
          <option key={t} value={t}>
            {DISCREPANCY_TYPE_LABELS[t]}
          </option>
        ))}
      </select>

      <select
        value={tableName}
        onChange={e => onTableChange(e.target.value)}
        className="text-sm border border-gray-300 rounded-md px-3 py-1.5"
      >
        <option value="">All Tables</option>
        <option value="substations">Substations</option>
        <option value="feeders">Feeders</option>
        <option value="transformers">Transformers</option>
        <option value="meters">Meters</option>
      </select>
    </div>
  )
}

// =============================================================================
// Main Dashboard
// =============================================================================

export function DiscrepancyDashboard() {
  const queryClient = useQueryClient()

  // Filters
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null)
  const [resolution, setResolution] = useState('')
  const [discrepancyType, setDiscrepancyType] = useState('')
  const [tableName, setTableName] = useState('')
  const [page, setPage] = useState(0)
  const pageSize = 50

  // Queries
  const { data: summary } = useQuery({
    queryKey: ['import-summary'],
    queryFn: getImportSummary,
    staleTime: 30_000,
  })

  const { data: runs } = useQuery({
    queryKey: ['import-runs'],
    queryFn: () => listImportRuns({ limit: 20 }),
    staleTime: 30_000,
  })

  const {
    data: discrepancies,
    isLoading: loadingDiscrepancies,
    error: discrepanciesError,
  } = useQuery({
    queryKey: ['discrepancies', selectedRunId, resolution, discrepancyType, tableName, page],
    queryFn: () => listDiscrepancies({
      skip: page * pageSize,
      limit: pageSize,
      run_id: selectedRunId ?? undefined,
      resolution: resolution || undefined,
      discrepancy_type: discrepancyType || undefined,
      table_name: tableName || undefined,
    }),
    staleTime: 15_000,
  })

  // Mutations. Accept-GIS now performs a real backend write (item 5) — on
  // success reflect the applied change on the map, the data-quality rollup, and
  // the affected entity's own detail query, not just the discrepancy list.
  const resolveMutation = useMutation({
    mutationFn: ({ id, res }: { id: number; res: Resolution; row: ImportDiscrepancy }) =>
      resolveDiscrepancy(id, res),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['discrepancies'] })
      queryClient.invalidateQueries({ queryKey: ['import-summary'] })
      if (variables.res === 'accept_gis') {
        invalidateAcceptGisTargets(queryClient, variables.row)
      }
    },
  })

  const handleResolve = useCallback((discrepancy: ImportDiscrepancy, res: Resolution) => {
    resolveMutation.mutate({ id: discrepancy.id, res, row: discrepancy })
  }, [resolveMutation])

  const resolving = resolveMutation.isPending

  return (
    <div className="flex-1 overflow-auto p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">GIS Import Discrepancies</h2>
            <p className="text-sm text-gray-500 mt-1">Review and resolve conflicts between GIS data and platform records</p>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && <SummaryCards summary={summary} />}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left: Import Run History */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Import Runs</h3>
              <ImportRunList
                runs={runs || []}
                selectedRunId={selectedRunId}
                onSelectRun={setSelectedRunId}
              />
            </div>
          </div>

          {/* Right: Discrepancy Table */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">
                  Discrepancies
                  {selectedRunId && <span className="text-gray-400 ml-1">(Run #{selectedRunId})</span>}
                </h3>

                {/* Bulk actions moved (UX Stage 1 C3 / D-8): the GIS Queue's
                    bulk flow previews the matched set and requires typing the
                    count before executing — no more one-click mass writes. */}
                <Link
                  to={appPath('/gis-queue')}
                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                  title="The GIS Queue previews the matched set and asks you to type the count before any bulk change runs"
                >
                  Bulk actions live in the GIS Queue &rarr;
                </Link>
              </div>

              <ActionExplainer />

              <Filters
                resolution={resolution}
                discrepancyType={discrepancyType}
                tableName={tableName}
                onResolutionChange={v => { setResolution(v); setPage(0) }}
                onTypeChange={v => { setDiscrepancyType(v); setPage(0) }}
                onTableChange={v => { setTableName(v); setPage(0) }}
              />

              {loadingDiscrepancies ? (
                <div className="text-sm text-gray-500 py-8 text-center">Loading...</div>
              ) : discrepanciesError ? (
                // Error state visually distinct from the empty state (spec §9): a
                // failed request must never read as "no discrepancies".
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-3 text-center">
                  Failed to load discrepancies
                  {discrepanciesError instanceof Error ? ` — ${discrepanciesError.message}` : ''}.
                  This is a request error, not an empty result.
                </div>
              ) : (
                <>
                  <DiscrepancyTable
                    discrepancies={discrepancies || []}
                    onResolve={handleResolve}
                    resolving={resolving}
                  />

                  {/* Pagination */}
                  {discrepancies && discrepancies.length > 0 && (
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="text-xs text-gray-500">
                        Page {page + 1} ({discrepancies.length} items)
                      </span>
                      <button
                        onClick={() => setPage(p => p + 1)}
                        disabled={discrepancies.length < pageSize}
                        className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
